import { jsPDF } from 'jspdf';

export const CONSULTATION_PDF_FONT_FAMILY = 'NotoSans';

const FONT_REGULAR_FILE = 'NotoSans-Regular.ttf';
const FONT_BOLD_FILE = 'NotoSans-Bold.ttf';

type CachedPdfFonts = {
  regularBase64: string;
  boldBase64: string;
};

let cachedFonts: CachedPdfFonts | null = null;
let fontsLoadPromise: Promise<CachedPdfFonts> | null = null;

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';

  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }

  return btoa(binary);
};

const fetchFontBase64 = async (path: string): Promise<string> => {
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Failed to load PDF font from ${path}`);
  }

  return arrayBufferToBase64(await response.arrayBuffer());
};

const loadPdfFontData = async (): Promise<CachedPdfFonts> => {
  if (cachedFonts) {
    return cachedFonts;
  }

  if (!fontsLoadPromise) {
    fontsLoadPromise = (async () => {
      const publicBaseUrl = process.env.PUBLIC_URL || '';
      const [regularBase64, boldBase64] = await Promise.all([
        fetchFontBase64(`${publicBaseUrl}/fonts/${FONT_REGULAR_FILE}`),
        fetchFontBase64(`${publicBaseUrl}/fonts/${FONT_BOLD_FILE}`),
      ]);

      cachedFonts = { regularBase64, boldBase64 };
      return cachedFonts;
    })();
  }

  return fontsLoadPromise;
};

export const applyConsultationPdfFonts = async (doc: jsPDF): Promise<void> => {
  const fonts = await loadPdfFontData();

  if (!doc.existsFileInVFS(FONT_REGULAR_FILE)) {
    doc.addFileToVFS(FONT_REGULAR_FILE, fonts.regularBase64);
    doc.addFont(FONT_REGULAR_FILE, CONSULTATION_PDF_FONT_FAMILY, 'normal');
  }

  if (!doc.existsFileInVFS(FONT_BOLD_FILE)) {
    doc.addFileToVFS(FONT_BOLD_FILE, fonts.boldBase64);
    doc.addFont(FONT_BOLD_FILE, CONSULTATION_PDF_FONT_FAMILY, 'bold');
  }
};

export const ensureConsultationPdfFonts = (): Promise<void> => (
  loadPdfFontData().then(() => undefined)
);
